import { StandardTable } from "@stenajs-webui/grid";
import { createTableConfig } from "../config/RebateFollowUpTableConfig";
import { RebateProgress, RebateProgressFilter } from "@/gql/graphql";
import { useContext, useCallback, useMemo } from "react";
import { RebatesFilterContext } from "./rebates-filter/RebatesFilterContext";
import { RebatesFilterContextType } from "../types/types";
import { CardBody, CardHeader, FlatButton } from "@stenajs-webui/elements";
import { Box } from "@stenajs-webui/core";
import { RebatesFilter } from "./rebates-filter/RebatesFilter";
import { useRebateProgress } from "../hooks/UseRebateProgress";
import { format } from "date-fns";
import { DateFormats } from "@/lib/dates/DateFormats";
import { rebatesTableMaxHeight } from "../lib/layout";
import { pluralize } from "@/lib/formatters/GeneralStringFormatting";

interface Props {
  onEdit: (rebateProgress: RebateProgress) => void;
}

export const RebateFollowUpView = ({ onEdit }: Props) => {
  const { rebateProgress, loadRebateProgress, loading } = useRebateProgress();

  const { filterState, setFilterIsOpen } = useContext(
    RebatesFilterContext
  ) as RebatesFilterContextType;

  const noFiltersSelected =
    filterState.selectedSalesRegions.length === 0 &&
    !filterState.selectedCustomer &&
    !filterState.selectedSeller &&
    !filterState.selectedValidity &&
    !filterState.selectedYear &&
    filterState.selectedTerms.length === 0;

  const onSearch = useCallback(() => {
    if (noFiltersSelected) {
      return;
    }

    const {
      selectedSalesRegions,
      selectedCustomer,
      selectedSeller,
      selectedYear,
      selectedValidity,
      selectedTerms,
    } = filterState;

    const filter: RebateProgressFilter = {
      ...(selectedCustomer?.value && {
        customerNumber: parseInt(selectedCustomer.value),
      }),
      ...(selectedSeller?.value && { sellerId: selectedSeller.value }),
      ...(selectedValidity?.startDate && {
        validFrom: format(selectedValidity.startDate, DateFormats.fullDate),
      }),
      ...(selectedYear?.value && { year: selectedYear.value }),
      ...(selectedValidity?.endDate && {
        validTo: format(selectedValidity.endDate, DateFormats.fullDate),
      }),
      ...(selectedSalesRegions.length > 0 && {
        salesRegionCodes: selectedSalesRegions.map((r) => parseInt(r.code)),
      }),
      ...(selectedTerms.length > 0 && {
        rebateTermCodes: selectedTerms.map((t) => t.id),
      }),
    };

    loadRebateProgress({ variables: { filter } });
  }, [filterState, loadRebateProgress, noFiltersSelected]);

  const config = useMemo(() => createTableConfig(onEdit), [onEdit]);

  return (
    <>
      <CardHeader
        text={
          rebateProgress.length > 0
            ? pluralize(rebateProgress.length, " rebate goal", " rebate goals")
            : "Rebate goals"
        }
      />
      <RebatesFilter onSearch={onSearch} />
      <Box overflow={"auto"} maxHeight={rebatesTableMaxHeight}>
        <CardBody>
          <StandardTable
            config={config}
            items={rebateProgress}
            loading={loading}
            noItemsLabel={
              noFiltersSelected
                ? "Please select a filter to search"
                : "No rebate agreement goals to show"
            }
            noItemsContentRight={
              <FlatButton
                label={"Open filter"}
                onClick={() => setFilterIsOpen(true)}
              />
            }
          />
        </CardBody>
      </Box>
    </>
  );
};
